import React, { useState, useEffect } from 'react';
import { FaTimes, FaCheckCircle, FaTimesCircle, FaClock, FaChartLine, FaInfoCircle } from 'react-icons/fa';
import HttpInterceptor from '../lib/HttpInterceptor';
import { formatDateLocale } from '../utils/dateFormatter';

/**
 * Progress Breakdown Modal Component
 * Shows detailed progress breakdown with class-by-class analysis
 */
function ProgressBreakdownModal({ isOpen, onClose, studentId, studentName, studentStatus, isConsultant }) {
  const [progressData, setProgressData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    // Detect PWA mode
    const checkPWA = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = window.navigator.standalone === true;
      const isInWebView = window.navigator.userAgent?.includes('wv');
      setIsPWA(isStandalone || isIOSStandalone || isInWebView);
    };
    checkPWA();
    window.addEventListener('resize', checkPWA);
    return () => window.removeEventListener('resize', checkPWA);
  }, []);

  useEffect(() => {
    if (isOpen && studentId) {
      fetchProgressData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, studentId]);

  const fetchProgressData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      
      // Check if student is pending approval
      if (studentStatus === 'pending') {
        setErrorMessage('This student is pending approval. Progress data will be available once the student is approved.');
        setIsLoading(false);
        return;
      }
      
      const response = await HttpInterceptor.get(`/api/students/${studentId}/progress`);
      setProgressData(response.data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
      
      // Extract specific error message from API response
      const errorResponse = error?.response?.data;
      let errorMsg = 'Failed to load progress data';
      
      if (errorResponse?.message) {
        const apiMessage = errorResponse.message.toLowerCase();
        
        if (apiMessage.includes('not found') || apiMessage.includes('student not found')) {
          errorMsg = isConsultant 
            ? 'Client not found. Please ensure the client has booked an appointment.'
            : 'Student not found. Please ensure the student is properly enrolled.';
        } else if (apiMessage.includes('not enrolled') || apiMessage.includes('enrolled in any program')) {
          errorMsg = isConsultant
            ? 'Client has not booked any appointments yet.'
            : 'Student is not enrolled in any program yet.';
        } else if (apiMessage.includes('unauthorized') || apiMessage.includes('not authorized')) {
          errorMsg = isConsultant
            ? 'You are not authorized to view this client\'s history.'
            : 'You are not authorized to view this student\'s progress.';
        } else if (apiMessage.includes('pending') || apiMessage.includes('approval')) {
          errorMsg = isConsultant
            ? 'This appointment is pending approval.'
            : 'This student is pending approval. Progress data will be available once the student is approved.';
        } else {
          errorMsg = errorResponse.message;
        }
      } else if (error?.message) {
        errorMsg = error.message;
      }
      
      setErrorMessage(errorMsg);
      // Don't show toast - error is displayed in the modal UI
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Using centralized date formatter

  const progress = progressData?.progress || {};
  const attendanceByClass = progressData?.attendanceByClass || [];
  const progressTimeline = progressData?.progressTimeline || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${isPWA ? 'bg-white border-2 border-orange-200/50 shadow-2xl' : 'bg-white shadow-xl'} rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col relative`}>
        {isPWA && (
          <>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100/30 to-amber-100/20 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-orange-50/20 to-transparent rounded-full blur-xl"></div>
          </>
        )}
        {/* Header */}
        <div className={`${isPWA ? 'bg-gradient-to-r from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-b-2 border-orange-200/50' : 'border-b border-gray-200'} flex items-center justify-between ${isPWA ? 'p-4 sm:p-6' : 'p-6'} relative z-10`}>
          <div>
            <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-800' : 'text-gray-900'} flex items-center gap-2`}>
              {isPWA && <FaChartLine className="text-orange-600" />}
              Progress Breakdown
            </h2>
            <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-800 font-semibold mt-1.5' : 'text-gray-700 mt-1'}`}>{studentName}</p>
          </div>
          <button
            onClick={onClose}
            className={`${isPWA ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-100/50' : 'text-gray-400 hover:text-gray-600'} focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-500' : 'focus:ring-blue-500'} rounded-lg ${isPWA ? 'p-2.5' : 'rounded-md p-2'} transition-all`}
            aria-label="Close modal"
          >
            <FaTimes className={isPWA ? 'h-5 w-5' : 'h-5 w-5'} />
          </button>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto ${isPWA ? 'p-4 sm:p-6 bg-white' : 'p-6'} relative z-10`}>
          {isLoading ? (
            <div className="text-center py-12">
              <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${isPWA ? 'border-orange-600' : 'border-blue-600'} mx-auto mb-4`}></div>
              <p className={isPWA ? 'text-orange-700' : 'text-gray-600'}>Loading progress data...</p>
            </div>
          ) : (
            <>
              {/* Overall Progress Summary */}
              <div className={`grid ${isPWA ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-4'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4'} mb-6`}>
                <div className={`${isPWA ? 'bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-blue-100/40 border-2 border-blue-200/60 shadow-lg hover:shadow-xl hover:border-blue-300/80' : 'bg-blue-50 border border-blue-200'} rounded-xl ${isPWA ? 'p-4 sm:p-5 relative overflow-hidden group' : 'p-4'} transition-all duration-300`}>
                  {isPWA && (
                    <>
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-indigo-400 to-blue-500"></div>
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-200/40 to-indigo-200/30 rounded-full blur-xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
                    </>
                  )}
                  <div className={isPWA ? 'relative z-10' : ''}>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-sm'} ${isPWA ? 'text-blue-700 uppercase tracking-wider font-bold' : 'text-blue-600 font-medium'}`}>
                      {isConsultant ? 'Total Appointments' : 'Total Classes'}
                    </p>
                    <p className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-2xl'} font-bold ${isPWA ? 'text-blue-600' : 'text-blue-700'} ${isPWA ? 'mt-2' : 'mt-1'}`}>{progress.totalClasses || 0}</p>
                  </div>
                </div>
                <div className={`${isPWA ? 'bg-gradient-to-br from-green-50/80 via-emerald-50/60 to-green-100/40 border-2 border-green-200/60 shadow-lg hover:shadow-xl hover:border-green-300/80' : 'bg-green-50 border border-green-200'} rounded-xl ${isPWA ? 'p-4 sm:p-5 relative overflow-hidden group' : 'p-4'} transition-all duration-300`}>
                  {isPWA && (
                    <>
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 via-emerald-400 to-green-500"></div>
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-200/40 to-emerald-200/30 rounded-full blur-xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
                    </>
                  )}
                  <div className={isPWA ? 'relative z-10' : ''}>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-sm'} ${isPWA ? 'text-green-700 uppercase tracking-wider font-bold' : 'text-green-600 font-medium'}`}>
                      {isConsultant ? 'Attended Sessions' : 'Attended'}
                    </p>
                    <p className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-2xl'} font-bold ${isPWA ? 'text-green-600' : 'text-green-700'} ${isPWA ? 'mt-2 flex items-center' : 'mt-1 flex items-center'}`}>
                      {isPWA && <FaCheckCircle className="mr-1.5 text-green-500" />}
                      {!isPWA && <FaCheckCircle className="mr-1" />}
                      {progress.completedClasses || 0}
                    </p>
                  </div>
                </div>
                <div className={`${isPWA ? 'bg-gradient-to-br from-red-50/80 via-rose-50/60 to-red-100/40 border-2 border-red-200/60 shadow-lg hover:shadow-xl hover:border-red-300/80' : 'bg-red-50 border border-red-200'} rounded-xl ${isPWA ? 'p-4 sm:p-5 relative overflow-hidden group' : 'p-4'} transition-all duration-300`}>
                  {isPWA && (
                    <>
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 via-rose-400 to-red-500"></div>
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-200/40 to-rose-200/30 rounded-full blur-xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
                    </>
                  )}
                  <div className={isPWA ? 'relative z-10' : ''}>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-sm'} ${isPWA ? 'text-red-700 uppercase tracking-wider font-bold' : 'text-red-600 font-medium'}`}>Missed</p>
                    <p className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-2xl'} font-bold ${isPWA ? 'text-red-600' : 'text-red-700'} ${isPWA ? 'mt-2 flex items-center' : 'mt-1 flex items-center'}`}>
                      {isPWA && <FaTimesCircle className="mr-1.5 text-red-500" />}
                      {!isPWA && <FaTimesCircle className="mr-1" />}
                      {progress.missedClasses || 0}
                    </p>
                  </div>
                </div>
                <div className={`${isPWA ? 'bg-gradient-to-br from-orange-50/80 via-amber-50/60 to-yellow-100/40 border-2 border-orange-200/60 shadow-lg hover:shadow-xl hover:border-orange-300/80' : 'bg-yellow-50 border border-yellow-200'} rounded-xl ${isPWA ? 'p-4 sm:p-5 relative overflow-hidden group' : 'p-4'} transition-all duration-300`}>
                  {isPWA && (
                    <>
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-200/40 to-amber-200/30 rounded-full blur-xl opacity-60 group-hover:opacity-100 transition-opacity"></div>
                    </>
                  )}
                  <div className={isPWA ? 'relative z-10' : ''}>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-sm'} ${isPWA ? 'text-orange-700 uppercase tracking-wider font-bold' : 'text-yellow-600 font-medium'}`}>Progress</p>
                    <p className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-600' : 'text-yellow-700'} ${isPWA ? 'mt-2' : 'mt-1'}`}>
                      {progress.progressPercentage || 0}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Progress Calculation Explanation */}
              <div className={`mb-6 ${isPWA ? 'p-4 sm:p-5 bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-yellow-50/20 border-2 border-orange-200/50 rounded-xl shadow-md' : 'p-4 bg-gray-50 border border-gray-200 rounded-lg'}`}>
                <div className="flex items-start">
                  {isPWA ? (
                    <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md flex-shrink-0">
                      <FaInfoCircle className="text-white text-base" />
                    </div>
                  ) : (
                    <FaInfoCircle className="text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                  )}
                  <div className={`${isPWA ? 'ml-3' : ''} ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-900' : 'text-gray-700'}`}>
                    <p className={`${isPWA ? 'font-bold mb-2 text-orange-800' : 'font-medium mb-1'}`}>
                      {isConsultant ? 'Appointment Tracking:' : 'Progress Calculation:'}
                    </p>
                    <p className={`${isPWA ? 'font-semibold text-orange-900' : 'text-gray-700'}`}>
                      {isConsultant 
                        ? 'Status = (Completed Appointments / Total Appointments) × 100'
                        : 'Progress = (Attended Sessions / Total Sessions) × 100'}
                    </p>
                    <p className={`${isPWA ? 'mt-2 text-orange-800 font-medium' : 'mt-1 text-gray-600'}`}>
                      {isConsultant 
                        ? `Summary: ${progress.completedClasses || 0} completed, ${progress.missedClasses || 0} cancelled, ${progress.excusedClasses || 0} rescheduled`
                        : `Breakdown: ${progress.completedClasses || 0} attended, ${progress.missedClasses || 0} missed, ${progress.excusedClasses || 0} excused`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Progress by Class */}
              {attendanceByClass.length > 0 && (
                <div className="mb-6">
                  <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-bold ${isPWA ? 'text-orange-800 mb-4 flex items-center gap-2' : 'font-semibold text-gray-900 mb-4'}`}>
                    {isPWA && <FaChartLine className="text-orange-600" />}
                    {isConsultant ? 'Service breakdown' : 'Progress by Class'}
                  </h3>
                  <div className={`${isPWA ? 'space-y-3 sm:space-y-4' : 'space-y-3'}`}>
                    {attendanceByClass.map((classItem, index) => (
                      <div
                        key={index}
                        className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 rounded-xl p-4 sm:p-5 shadow-lg hover:shadow-xl hover:border-orange-300/80' : 'border border-gray-200 rounded-lg p-4 hover:shadow-md'} transition-all duration-300 relative overflow-hidden group`}
                      >
                        {isPWA && (
                          <>
                            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-xl"></div>
                          </>
                        )}
                        <div className={`flex ${isPWA ? 'flex-col sm:flex-row' : 'items-center'} justify-between ${isPWA ? 'mb-3 gap-3' : 'mb-3'} relative z-10`}>
                          <div>
                            <p className={`${isPWA ? 'text-sm sm:text-base' : 'font-semibold'} ${isPWA ? 'font-bold text-gray-900' : 'text-gray-900'}`}>
                              {classItem.class?.classType || 'N/A'} - {classItem.class?.day || 'N/A'} {classItem.class?.time || ''}
                            </p>
                          </div>
                          <div className={`flex ${isPWA ? 'items-center gap-3 sm:gap-4' : 'items-center gap-4'} ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'}`}>
                            <span className={`${isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50 px-2.5 py-1.5 rounded-lg shadow-sm font-bold' : 'text-green-600 font-medium'}`}>
                              <FaCheckCircle className={`${isPWA ? 'inline mr-1.5' : 'inline mr-1'}`} />
                              {classItem.attendedCount || 0}
                            </span>
                            <span className={`${isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50 px-2.5 py-1.5 rounded-lg shadow-sm font-bold' : 'text-red-600 font-medium'}`}>
                              <FaTimesCircle className={`${isPWA ? 'inline mr-1.5' : 'inline mr-1'}`} />
                              {classItem.missedCount || 0}
                            </span>
                            <span className={`${isPWA ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border border-yellow-200/50 px-2.5 py-1.5 rounded-lg shadow-sm font-bold' : 'text-yellow-600 font-medium'}`}>
                              <FaClock className={`${isPWA ? 'inline mr-1.5' : 'inline mr-1'}`} />
                              {classItem.excusedCount || 0}
                            </span>
                          </div>
                        </div>
                        {classItem.attendanceRecords && classItem.attendanceRecords.length > 0 && (
                          <div className={`mt-3 pt-3 ${isPWA ? 'border-t-2 border-orange-200/50' : 'border-t border-gray-100'} relative z-10`}>
                            <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} ${isPWA ? 'text-orange-800 font-bold mb-2' : 'text-gray-600 mb-2 font-medium'}`}>Recent Attendance:</p>
                            <div className={`${isPWA ? 'space-y-2' : 'space-y-1'}`}>
                              {classItem.attendanceRecords.slice(0, 5).map((record, recordIndex) => (
                                <div key={recordIndex} className={`flex items-center justify-between ${isPWA ? 'text-[10px] sm:text-xs bg-white/80 rounded-lg px-2 py-1.5 border border-gray-200/50' : 'text-xs'}`}>
                                  <span className={isPWA ? 'text-gray-800 font-semibold' : 'text-gray-700'}>
                                    {formatDateLocale(record.sessionDate)}
                                  </span>
                                  <span className={`${isPWA ? 'px-2.5 py-1 rounded-full font-bold shadow-sm' : 'px-2 py-0.5 rounded-full'} ${
                                    record.status === 'attended' ? isPWA ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200/50' : 'bg-green-100 text-green-800' :
                                    record.status === 'missed' ? isPWA ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200/50' : 'bg-red-100 text-red-800' :
                                    record.status === 'scheduled' ? isPWA ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200/50' : 'bg-blue-100 text-blue-800' :
                                    isPWA ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border border-yellow-200/50' : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {isConsultant && record.status === 'attended' ? 'completed' : 
                                     isConsultant && record.status === 'missed' ? 'cancelled' : 
                                     record.status}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress Timeline */}
              {progressTimeline.length > 0 && (
                <div>
                  <h3 className={`${isPWA ? 'text-base sm:text-lg' : 'text-lg'} font-bold ${isPWA ? 'text-orange-800 mb-4' : 'font-semibold text-gray-900 mb-4'} flex items-center gap-2`}>
                    <FaChartLine className={isPWA ? 'text-orange-600' : ''} />
                    Progress Timeline
                  </h3>
                  <div className={`${isPWA ? 'border-2 border-orange-200/50 rounded-xl overflow-hidden shadow-lg' : 'border border-gray-200 rounded-lg overflow-hidden'}`}>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className={isPWA ? 'bg-gradient-to-r from-orange-50/50 via-amber-50/30 to-yellow-50/20' : 'bg-gray-50'}>
                          <tr>
                            <th className={`${isPWA ? 'px-3 sm:px-4 py-3' : 'px-4 py-3'} text-left ${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} font-bold ${isPWA ? 'text-orange-700 uppercase tracking-wider' : 'font-medium text-gray-500 uppercase'}`}>Period</th>
                            <th className={`${isPWA ? 'px-3 sm:px-4 py-3' : 'px-4 py-3'} text-left ${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} font-bold ${isPWA ? 'text-orange-700 uppercase tracking-wider' : 'font-medium text-gray-500 uppercase'}`}>Attended</th>
                            <th className={`${isPWA ? 'px-3 sm:px-4 py-3' : 'px-4 py-3'} text-left ${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} font-bold ${isPWA ? 'text-orange-700 uppercase tracking-wider' : 'font-medium text-gray-500 uppercase'}`}>Total</th>
                            <th className={`${isPWA ? 'px-3 sm:px-4 py-3' : 'px-4 py-3'} text-left ${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} font-bold ${isPWA ? 'text-orange-700 uppercase tracking-wider' : 'font-medium text-gray-500 uppercase'}`}>Progress</th>
                          </tr>
                        </thead>
                        <tbody className={`${isPWA ? 'bg-white/60 divide-y divide-orange-100/50' : 'bg-white divide-y divide-gray-200'}`}>
                          {progressTimeline.map((timeline, index) => (
                            <tr key={index} className={isPWA ? 'hover:bg-orange-50/50 transition-colors' : ''}>
                              <td className={`${isPWA ? 'px-3 sm:px-4 py-3' : 'px-4 py-3'} ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'font-semibold text-gray-900' : 'text-gray-900'}`}>{timeline.period || 'N/A'}</td>
                              <td className={`${isPWA ? 'px-3 sm:px-4 py-3' : 'px-4 py-3'} ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'font-semibold text-gray-700' : 'text-gray-600'}`}>{timeline.attended || 0}</td>
                              <td className={`${isPWA ? 'px-3 sm:px-4 py-3' : 'px-4 py-3'} ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'font-semibold text-gray-700' : 'text-gray-600'}`}>{timeline.total || 0}</td>
                              <td className={`${isPWA ? 'px-3 sm:px-4 py-3' : 'px-4 py-3'} ${isPWA ? 'text-xs sm:text-sm' : 'text-sm'}`}>
                                <div className="flex items-center">
                                  <div className={`${isPWA ? 'w-20 sm:w-24' : 'w-24'} ${isPWA ? 'bg-orange-100/50 border border-orange-200/50' : 'bg-gray-200'} rounded-full ${isPWA ? 'h-2.5 shadow-inner' : 'h-2'} mr-2`}>
                                    <div
                                      className={`${isPWA ? 'bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 h-2.5 shadow-md' : 'bg-blue-600 h-2'} rounded-full transition-all duration-500`}
                                      style={{ width: `${timeline.progress || 0}%` }}
                                    ></div>
                                  </div>
                                  <span className={`${isPWA ? 'font-bold text-orange-700' : 'text-gray-900 font-medium'}`}>{timeline.progress || 0}%</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {!progressData && !isLoading && (
                <div className={`text-center py-8 ${isPWA ? 'bg-gradient-to-br from-orange-50/80 via-amber-50/60 to-yellow-50/40 border-2 border-orange-200/50 rounded-xl' : 'bg-gray-50 rounded-lg'}`}>
                  {errorMessage ? (
                    <div>
                      <p className={`${isPWA ? 'text-orange-800 font-bold mb-2' : 'text-gray-700 font-semibold mb-2'}`}>
                        {errorMessage}
                      </p>
                      {studentStatus === 'pending' && (
                        <p className={`${isPWA ? 'text-orange-600 text-sm' : 'text-gray-500 text-sm'}`}>
                          {isConsultant 
                            ? 'Please approve the appointment to enable tracking.' 
                            : 'Please approve the student to enable progress tracking.'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className={isPWA ? 'text-orange-800 font-bold' : 'text-gray-600 font-medium'}>No progress data available</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={`${isPWA ? 'border-t-2 border-orange-200/50 bg-gradient-to-r from-orange-50/30 via-amber-50/20 to-yellow-50/10' : 'border-t border-gray-200'} p-4 flex justify-end relative z-10`}>
          <button
            onClick={onClose}
            className={`${isPWA ? 'px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg border border-orange-400/50 font-bold' : 'px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-lg transition-all duration-200`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProgressBreakdownModal;

